import React, { useState } from 'react';
import PageShell, { Button, Notice } from './PageShell';
import Table from './Table';
import Modal, { ConfirmModal } from './Modal';
import Card, { StatCard, StatGrid, EmptyState } from './Card';
import Tabs from './Tabs';
import { FormGrid, Field, Input, Textarea, Select, Checkbox, RadioGroup, FormSection } from './Form';
import SearchableSelect from './SearchableSelect';
import './showcase.css';

/**
 * STATIC design showcase — no API, no database, no app state.
 *
 * Its job is to settle the VISUAL LANGUAGE once, in one place, using
 * fake data. Every cell type that exists anywhere in the real app is
 * represented here, so we can agree what a table looks like before any
 * real page is rebuilt on it:
 *
 *   - plain text, strong text, muted/empty values
 *   - a "title" cell with a name plus secondary badges under it (exams)
 *   - info badges for grade/section (exams)
 *   - status badges with an icon (exams: منشور/مسودة)
 *   - numeric + percentage cells (exams: العلامة/الوزن)
 *   - a no-wrap column (phone numbers, dates, ids)
 *   - a long free-text column that has to wrap (address/notes)
 *   - icon-only row actions AND a wide multi-button action group
 *   - a second table that is deliberately narrow, to show that spare
 *     width stays EMPTY instead of stretching the columns
 */

const STUDENTS = [
  { id: 1, name: 'محمود حسين محمد', phone: '+963911000001', grade: 'الصف الأول', section: 'أولى م', address: 'كفر كلبين جانب جامع أدريس القرني، خلف المدرسة القديمة', status: 'active', locked: false, fees: 550000, paid: 550000 },
  { id: 2, name: 'حسناء احمد عساف', phone: '+963945286278', grade: 'الصف الأول', section: 'ثانية م', address: 'الصناعة مقابل ملعب الشبيبة، منازل أولاد رسلان', status: 'active', locked: true, fees: 550000, paid: 200000 },
  { id: 3, name: 'أسيل غسان عبود', phone: '+963981688732', grade: 'الصف الثاني', section: 'أولى م', address: 'شارع فرن الذرة، وادي حمادة', status: 'active', locked: false, fees: 500000, paid: 0 },
  { id: 4, name: 'بدر الدين محمد السيد علي', phone: '+963998938793', grade: 'الصف الثاني', section: 'ثالثة م', address: '', status: 'inactive', locked: false, fees: 500000, paid: 500000 },
  { id: 5, name: 'فضيلة فؤاد العلي', phone: '+963951334569', grade: 'الصف الثالث', section: 'أولى م', address: 'دوار الساعة جانب السمان الجميل', status: 'active', locked: false, fees: 600000, paid: 300000 },
];

const EXAMS = [
  { id: 1, title: 'امتحان الفصل الأول', semester: 'فصل أول', type: 'امتحان', subject: 'اللغة العربية', grade: 'الصف السابع', section: 'أولى م', date: '2026-10-12', total: 100, weight: 40, published: true },
  { id: 2, title: 'مذاكرة القراءة', semester: 'فصل أول', type: 'مذاكرة أولى', subject: 'اللغة الإنكليزية', grade: 'الصف الثامن', section: 'أولى ص', date: '2026-10-20', total: 20, weight: 10, published: false },
  { id: 3, title: 'سبر شفهي في قواعد الجبر والهندسة', semester: 'فصل ثاني', type: 'شفهي', subject: 'الرياضيات', grade: 'الصف التاسع', section: 'ثانية م', date: '2026-11-03', total: 10, weight: 5, published: false },
];

const money = (n) => `$${n.toLocaleString('en-US')}`;

export default function Showcase() {
  const [tone, setTone] = useState('wide');
  const [showLoading, setShowLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tab, setTab] = useState('info');
  const [checked, setChecked] = useState(true);
  const [radio, setRadio] = useState('male');
  const [ssel, setSsel] = useState('');

  return (
    <PageShell
      title="معرض التصميم"
      description="صفحة ثابتة ببيانات تجريبية — لتثبيت شكل الجداول والعناصر قبل تطبيقها على صفحات النظام"
      actions={
        <>
          <Button variant="secondary" onClick={() => setShowLoading((v) => !v)}>
            {showLoading ? 'إخفاء حالة التحميل' : 'إظهار حالة التحميل'}
          </Button>
          <Button variant="secondary" onClick={() => setTone(tone === 'wide' ? 'narrow' : 'wide')}>
            {tone === 'wide' ? 'إظهار جدول ضيق' : 'إظهار جدول عريض'}
          </Button>
          <Button>+ عنصر جديد</Button>
        </>
      }
    >
      <section className="sc-section">
        <h2 className="sc-heading">جدول كامل — جميع أنواع الخلايا</h2>
        <Table
          keyField="id"
          rows={STUDENTS}
          loading={showLoading}
          statusKey="status"
          searchPlaceholder="ابحث بالاسم أو الهاتف..."
          onEdit={() => {}}
          onDelete={() => {}}
          renderActions={() => (
            <button type="button" className="ui-icon-btn ui-icon-btn-warn" title="إيقاف الحساب">🔒</button>
          )}
          columns={[
            {
              key: 'name',
              label: 'الطالب',
              width: 2,
              filterable: false,
              render: (r) => (
                <div className="sc-title-cell">
                  <strong>{r.name}</strong>
                  <div className="sc-title-sub">
                    <span className="ui-badge ui-badge-neutral">{r.grade}</span>
                    <span className="ui-badge ui-badge-info">{r.section}</span>
                  </div>
                </div>
              ),
            },
            { key: 'phone', label: 'رقم الهاتف', nowrap: true, filterable: false },
            // Long free text: wraps at spaces, capped at 2 lines — the row
            // is already 2-3 lines tall because of the name + badges cell,
            // so this uses height that exists instead of width that doesn't.
            { key: 'address', label: 'العنوان', width: 3, clamp: 2, filterable: false },
            {
              key: 'paid',
              label: 'المدفوع',
              nowrap: true,
              align: 'end',
              filterable: false,
              render: (r) => {
                const done = r.paid >= r.fees;
                const partial = r.paid > 0 && !done;
                return (
                  <span className={`ui-badge ${done ? 'ui-badge-success' : partial ? 'ui-badge-warning' : 'ui-badge-danger'}`}>
                    {money(r.paid)}
                  </span>
                );
              },
            },
            {
              key: 'locked',
              label: 'الدخول',
              align: 'center',
              nowrap: true,
              render: (r) => (
                <span className={`ui-badge ${r.locked ? 'ui-badge-danger' : 'ui-badge-success'}`}>
                  {r.locked ? '🔒 موقوف' : '🔓 نشط'}
                </span>
              ),
            },
            {
              key: 'status',
              label: 'الحالة',
              align: 'center',
              nowrap: true,
              badge: (v) => (v === 'active' ? 'success' : 'neutral'),
              formatValue: (v) => (v === 'active' ? 'نشط' : 'غير نشط'),
            },
          ]}
        />
      </section>

      <section className="sc-section">
        <h2 className="sc-heading">جدول الامتحانات — عنوان مركّب وأزرار إجراءات عريضة</h2>
        <Table
          keyField="id"
          rows={EXAMS}
          searchPlaceholder="ابحث في الامتحانات..."
          actionsLabel="الإجراءات"
          renderActions={(r) => (
            <div className="sc-action-group">
              <button type="button" className="ui-btn-mini">العلامات</button>
              {r.published
                ? <button type="button" className="ui-btn-mini ui-btn-mini-warn">إلغاء النشر</button>
                : <button type="button" className="ui-btn-mini ui-btn-mini-primary">نشر</button>}
            </div>
          )}
          columns={[
            {
              key: 'title',
              label: 'العنوان',
              width: 2,
              filterable: false,
              render: (r) => (
                <div className="sc-title-cell">
                  <strong>{r.title}</strong>
                  <div className="sc-title-sub">
                    <span className="ui-badge ui-badge-neutral">{r.semester}</span>
                    <span className="ui-badge ui-badge-info">{r.type}</span>
                  </div>
                </div>
              ),
            },
            { key: 'subject', label: 'المادة', width: 1 },
            { key: 'section', label: 'الشعبة', align: 'center', nowrap: true, render: (r) => <span className="ui-badge ui-badge-neutral">{r.section}</span> },
            { key: 'date', label: 'التاريخ', nowrap: true, filterable: false },
            { key: 'total', label: 'العلامة', align: 'center', nowrap: true, filterable: false, render: (r) => <strong>{r.total}</strong> },
            { key: 'weight', label: 'الوزن', align: 'center', nowrap: true, filterable: false, formatValue: (v) => `${v}%` },
            {
              key: 'published',
              label: 'النشر',
              align: 'center',
              nowrap: true,
              render: (r) => (
                <span className={`ui-badge ${r.published ? 'ui-badge-success' : 'ui-badge-warning'}`}>
                  {r.published ? '🔒 منشور' : '✏️ مسودة'}
                </span>
              ),
            },
          ]}
        />
      </section>

      {tone === 'narrow' && (
        <section className="sc-section">
          <h2 className="sc-heading">جدول ضيق — المساحة الفارغة تبقى فارغة</h2>
          <p className="sc-note">
            ثلاثة أعمدة قصيرة فقط: الأعمدة تبقى بحجمها الطبيعي ولا تتمدّد لملء الشاشة.
          </p>
          <Table
            keyField="id"
            rows={[
              { id: 1, code: 'B1', name: 'صباحي', count: 412 },
              { id: 2, code: 'B2', name: 'مسائي', count: 279 },
            ]}
            columns={[
              { key: 'code', label: 'الرمز', nowrap: true, filterable: false },
              { key: 'name', label: 'الفوج', nowrap: true },
              { key: 'count', label: 'عدد الطلاب', align: 'end', nowrap: true, filterable: false },
            ]}
          />
        </section>
      )}

      <section className="sc-section">
        <h2 className="sc-heading">بطاقات الإحصاء</h2>
        <StatGrid>
          <StatCard label="إجمالي الطلاب" value="691" icon="🎓" tone="primary" />
          <StatCard label="الحاضرون اليوم" value="654" icon="✅" tone="success" hint="94.6%" />
          <StatCard label="الغائبون" value="37" icon="⚠️" tone="danger" />
          <StatCard label="المعلمون" value="28" icon="👨‍🏫" tone="info" />
          <StatCard label="أقساط غير مسددة" value="1,240,000" icon="💰" tone="warning" />
        </StatGrid>
      </section>

      <section className="sc-section">
        <h2 className="sc-heading">التبويبات والبطاقات</h2>
        <Tabs
          items={[
            { id: 'info', label: 'البيانات الأساسية' },
            { id: 'attendance', label: 'الحضور', badge: 3 },
            { id: 'exams', label: 'العلامات' },
            { id: 'empty', label: 'قسم فارغ' },
          ]}
          active={tab}
          onChange={setTab}
        />
        <Card title={tab === 'empty' ? 'لا يوجد محتوى' : 'محتوى التبويب'}>
          {tab === 'empty' ? (
            <EmptyState
              title="لا توجد بيانات بعد"
              message="عند إضافة أول عنصر سيظهر هنا."
              action={<Button>+ إضافة عنصر</Button>}
            />
          ) : (
            <p style={{ margin: 0, fontSize: 'var(--ui-text-sm)', color: 'var(--ui-ink-soft)' }}>
              محتوى تبويب «{tab}» — البطاقة هي الحاوية الموحّدة لأي شيء ليس جدولاً.
            </p>
          )}
        </Card>
      </section>

      <section className="sc-section">
        <h2 className="sc-heading">النماذج</h2>
        <Card>
          <FormSection title="بيانات الطالب">
            <FormGrid columns={3}>
              <Field label="الاسم الكامل" required>
                <Input placeholder="أحمد محمد" defaultValue="محمود حسين محمد" />
              </Field>
              <Field label="رقم الهاتف" hint="يُستخدم لإرسال رمز الدخول">
                <Input type="tel" placeholder="+963..." dir="ltr" />
              </Field>
              <Field label="تاريخ الميلاد">
                <Input type="date" defaultValue="2018-04-10" />
              </Field>
              <Field label="الرقم الوطني" error="الرقم الوطني يجب أن يكون 11 رقماً">
                <Input defaultValue="3303938" error />
              </Field>
              <Field label="الصف" required>
                <Select placeholder="اختر الصف" options={['الصف الأول', 'الصف الثاني', 'الصف الثالث']} />
              </Field>
              <Field
                label="بحث عن طالب (المكوّن الجديد)"
                hint="اكتب مباشرة للبحث، أو اضغط للتصفّح — بلا نافذة بحث منفصلة"
              >
                <SearchableSelect
                  value={ssel}
                  onChange={setSsel}
                  placeholder="اختر أو ابحث عن طالب..."
                  options={STUDENTS.map((s) => ({ value: s.id, label: `${s.name} — ${s.grade}` }))}
                />
              </Field>
              <Field label="القسط الشهري">
                <Input type="number" defaultValue={550000} />
              </Field>
              <Field label="الجنس">
                <RadioGroup
                  value={radio}
                  onChange={setRadio}
                  options={[{ value: 'male', label: 'ذكر' }, { value: 'female', label: 'أنثى' }]}
                />
              </Field>
              <Field label="الحالة">
                <Checkbox label="حساب نشط" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
              </Field>
              <Field label="حقل معطّل">
                <Input defaultValue="غير قابل للتعديل" disabled />
              </Field>
              <Field label="العنوان" span>
                <Textarea placeholder="العنوان بالتفصيل..." defaultValue="كفر كلبين جانب جامع أدريس القرني" />
              </Field>
            </FormGrid>
          </FormSection>
          <div style={{ display: 'flex', gap: 'var(--ui-space-2)' }}>
            <Button variant="success">حفظ</Button>
            <Button variant="secondary">إلغاء</Button>
          </div>
        </Card>
      </section>

      <section className="sc-section">
        <h2 className="sc-heading">النوافذ المنبثقة</h2>
        <div className="sc-swatch-row">
          <Button onClick={() => setModalOpen(true)}>فتح نافذة</Button>
          <Button variant="danger" onClick={() => setConfirmOpen(true)}>تأكيد حذف</Button>
        </div>
        <Modal
          open={modalOpen}
          title="تعديل بيانات"
          onClose={() => setModalOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>إلغاء</Button>
              <Button variant="success" onClick={() => setModalOpen(false)}>حفظ</Button>
            </>
          }
        >
          <FormGrid columns={2}>
            <Field label="الاسم" required><Input defaultValue="أحمد الشيخ علي" /></Field>
            <Field label="الهاتف"><Input dir="ltr" defaultValue="+963911000001" /></Field>
            <Field label="ملاحظات" span><Textarea rows={3} /></Field>
          </FormGrid>
        </Modal>
        <ConfirmModal
          open={confirmOpen}
          title="تأكيد الحذف"
          message="سيتم تغيير حالة هذا العنصر إلى غير نشط. هل تريد المتابعة؟"
          confirmText="نعم، احذف"
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => setConfirmOpen(false)}
        />
      </section>

      <section className="sc-section">
        <h2 className="sc-heading">العناصر المفردة</h2>
        <div className="sc-swatches">
          <div className="sc-swatch">
            <span className="sc-swatch-label">الأزرار</span>
            <div className="sc-swatch-row">
              <Button>أساسي</Button>
              <Button variant="secondary">ثانوي</Button>
              <Button variant="success">حفظ</Button>
              <Button variant="danger">حذف</Button>
              <Button disabled>معطّل</Button>
            </div>
          </div>
          <div className="sc-swatch">
            <span className="sc-swatch-label">أزرار قيد التنفيذ (loading)</span>
            <div className="sc-swatch-row">
              <Button variant="success" loading>حفظ</Button>
              <Button variant="secondary" loading>إلغاء</Button>
              <Button variant="danger" loading>حذف</Button>
            </div>
          </div>
          <div className="sc-swatch">
            <span className="sc-swatch-label">الشارات</span>
            <div className="sc-swatch-row">
              <span className="ui-badge ui-badge-success">نشط</span>
              <span className="ui-badge ui-badge-danger">موقوف</span>
              <span className="ui-badge ui-badge-warning">معلّق</span>
              <span className="ui-badge ui-badge-info">معلومة</span>
              <span className="ui-badge ui-badge-neutral">محايد</span>
            </div>
          </div>
          <div className="sc-swatch">
            <span className="sc-swatch-label">أزرار الصف</span>
            <div className="sc-swatch-row">
              <button className="ui-icon-btn ui-icon-btn-edit" title="تعديل">✎</button>
              <button className="ui-icon-btn ui-icon-btn-delete" title="حذف">✕</button>
              <button className="ui-icon-btn ui-icon-btn-warn" title="إيقاف">🔒</button>
            </div>
          </div>
        </div>
        <div className="sc-notices">
          <Notice tone="info">معلومة عادية.</Notice>
          <Notice tone="warning">تحذير يحتاج انتباه.</Notice>
          <Notice tone="danger">إجراء لا يمكن التراجع عنه.</Notice>
        </div>
      </section>
    </PageShell>
  );
}
